import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

interface DepartmentItem {
  id: number
  nome: string
  count: number
  totalCost: number
}

interface DepartmentHeadcountChartProps {
  data: DepartmentItem[] | undefined
  isLoading: boolean
}

const formatCurrency = (value: number) => {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}k`
  return `R$ ${value.toFixed(0)}`
}

export function DepartmentHeadcountChart({ data, isLoading }: DepartmentHeadcountChartProps) {
  if (isLoading) {
    return (
      <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 animate-pulse shadow-monumenta">
        <div className="mb-6">
          <div className="h-6 w-56 bg-brand-dark-lighter rounded mb-2" />
          <div className="h-4 w-64 bg-brand-dark-lighter rounded" />
        </div>
        <div className="h-[350px] bg-brand-dark-lighter rounded" />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 shadow-monumenta">
        <div className="mb-6">
          <h3 className="text-white font-display text-xl font-semibold mb-1">Headcount por Departamento</h3>
          <p className="text-brand-gray font-body text-sm">Colaboradores ativos por setor</p>
        </div>
        <div className="flex items-center justify-center h-[350px]">
          <p className="text-brand-gray font-body text-sm">Nenhum dado disponível</p>
        </div>
      </div>
    )
  }

  // Truncate long department names for chart readability
  const chartData = data.map((d) => ({
    ...d,
    label: d.nome.length > 25 ? d.nome.substring(0, 22) + '...' : d.nome,
  }))

  // Dynamic height based on number of departments
  const barHeight = 32
  const chartHeight = Math.max(300, chartData.length * barHeight + 80)

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 shadow-monumenta">
      <div className="mb-6">
        <h3 className="text-white font-display text-xl font-semibold mb-1">Headcount por Departamento</h3>
        <p className="text-brand-gray font-body text-sm">Colaboradores ativos por setor</p>
      </div>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2A2D34" horizontal={false} />
          <XAxis
            type="number"
            stroke="#838998"
            tick={{ fontSize: 11, fill: '#838998' }}
          />
          <YAxis
            type="category"
            dataKey="label"
            stroke="#838998"
            tick={{ fontSize: 11, fill: '#838998' }}
            width={160}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const item = payload[0]?.payload as DepartmentItem & { label: string }
                return (
                  <div
                    style={{
                      backgroundColor: '#24262B',
                      border: '1px solid #2A2D34',
                      borderRadius: '12px',
                      padding: '10px',
                      color: '#ffffff',
                    }}
                  >
                    <p style={{ fontWeight: 600, marginBottom: '5px' }}>{item.nome}</p>
                    <p style={{ color: '#D2FA52', marginBottom: '3px' }}>
                      {item.count} colaboradores
                    </p>
                    <p style={{ color: '#838998' }}>
                      Custo total: {formatCurrency(item.totalCost)}
                    </p>
                  </div>
                )
              }
              return null
            }}
          />
          <Bar dataKey="count" fill="#D2FA52" radius={[0, 4, 4, 0]} barSize={20} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
